<script lang="ts">
  import { onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api/client';
  import type { CheckIn, Member, Place } from '../lib/api/types';
  import { groups } from '../lib/stores/groups';
  import { isAuthenticated } from '../lib/stores/auth';
  import PageHeader from '../lib/components/PageHeader.svelte';
  import { clockAt, left } from '../lib/utils/checkin';
  import { t } from '../lib/i18n/index.ts';

  // CheckIn is the member's check-in timer (spec 004): start one
  // naming people from their groups, then see the time left, add time, or
  // check in; after it ran out, see who was alerted and check in.
  const LENGTHS = [15, 30, 60, 120];
  const ADDS = [15, 30, 60];

  let current = $state<CheckIn | null>(null);
  let loaded = $state(false);
  let people = $state<Member[]>([]);
  let chosen = $state<string[]>([]);
  let minutes = $state(30);
  let custom = $state('');
  let note = $state('');
  // watch also ends the timer on leaving or reaching a place (spec 003):
  // a group place, or 'here' for where the member is now.
  let watch = $state<'' | 'leave' | 'arrive'>('');
  let watchPlace = $state('here');
  let places = $state<Place[]>([]);
  // Reaching where the member already is means nothing, so reaching needs a place.
  $effect(() => {
    if (watch === 'arrive' && watchPlace === 'here') watchPlace = places[0]?.id ?? '';
  });
  let error = $state('');
  let busy = $state(false);
  let now = $state(Date.now());
  const tick = setInterval(() => (now = Date.now()), 1_000);
  // Another door may change the timer; it is read again every 30 seconds.
  const poll = setInterval(load, 30_000);
  onDestroy(() => {
    clearInterval(tick);
    clearInterval(poll);
  });

  $effect(() => {
    if (!$isAuthenticated) navigate('/login?next=/check-in', { replace: true });
  });

  load();
  loadPeople();

  async function load() {
    try {
      const got = await api.get<{ current: CheckIn | null; last_recipients: string[] }>('/me/check-in');
      current = got.current;
      if (!loaded) chosen = got.last_recipients;
      loaded = true;
    } catch (e: any) {
      error = e.message;
    }
  }

  async function loadPeople() {
    try {
      const gs = await groups.load();
      const seen = new Map<string, Member>();
      const me = (await api.get<{ id: string }>('/auth/me')).id;
      for (const g of gs) for (const m of await api.get<Member[]>(`/groups/${g.id}/members`)) if (m.user_id !== me) seen.set(m.user_id, m);
      people = [...seen.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
      places = (await Promise.all(gs.map((g) => api.get<Place[]>(`/groups/${g.id}/places`)))).flat().sort((a, b) => a.name.localeCompare(b.name));
      chosen = chosen.filter((id) => seen.has(id));
    } catch (e: any) {
      error = e.message;
    }
  }

  async function act(fn: () => Promise<CheckIn>) {
    busy = true;
    try {
      const got = await fn();
      current = got.state === 'running' || got.state === 'alerted' ? got : null;
      error = '';
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }

  // here asks the browser where the member is, or leaves it to the server's
  // latest report when it cannot say within 10 seconds.
  function here(): Promise<Record<string, number>> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy_m: p.coords.accuracy }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
      );
    });
  }

  function start(e: SubmitEvent) {
    e.preventDefault();
    const m = custom ? Math.round(Number(custom)) : minutes;
    act(async () => {
      const body: Record<string, unknown> = { minutes: m, recipients: chosen, note };
      if (watch) {
        body.watch = watch;
        if (watchPlace === 'here') body.here = await here();
        else body.place_id = watchPlace;
      }
      return api.post<CheckIn>('/me/check-in', body);
    });
  }
</script>

<div class="p-6 max-w-2xl w-full mx-auto space-y-5">
  <PageHeader title={t('checkinTitle')} />
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if !loaded}
    <p class="text-text-muted">{t('checkinLoading')}</p>
  {:else if current?.state === 'running'}
    <section class="card p-5 space-y-4" aria-live="polite">
      <p class="text-sm text-text-muted">{t('checkinBy')}</p>
      <p class="text-4xl font-semibold">{clockAt(current.due_at)}</p>
      <p class="text-lg">{left(current.due_at, now)}</p>
      {#if current.note}<p>{current.note}</p>{/if}
      {#if current.watch}
        <p class="text-sm">{current.watch_name ? t('checkinEndsWhen', { watch: current.watch, place: current.watch_name }) : t('checkinEndsWhenStart')}</p>
        {#if current.watch_note}<p class="text-sm" style="color: var(--theme-warning);">{current.watch_note}</p>{/if}
      {/if}
      <p class="text-sm text-text-muted">{t('checkinIfNot', { people: current.recipients?.map((r) => r.display_name).join(', ') ?? '' })}</p>
      <div class="flex flex-wrap gap-2">
        {#each ADDS as m (m)}
          <button class="btn btn-secondary" disabled={busy} onclick={() => act(() => api.post<CheckIn>(`/me/check-in/${current!.id}/extend`, { minutes: m }))}>{m < 60 ? t('checkinAddMinutes', { minutes: m }) : t('checkinAddHours', { hours: m / 60 })}</button>
        {/each}
      </div>
      <button class="btn btn-primary w-full text-lg py-3" disabled={busy} onclick={() => act(() => api.post<CheckIn>(`/me/check-in/${current!.id}/done`))}>{t('checkinDone')}</button>
    </section>
  {:else if current?.state === 'alerted'}
    <section class="card p-5 space-y-4">
      <p class="text-xl font-semibold" style="color: var(--theme-error);">{t('checkinPassed')}</p>
      <!-- Only those who still shared a group when it went out were alerted (004 FR-034). -->
      {#if current.alerted?.length}
        <p>{t('checkinAlerted', { count: current.alerted.length, people: current.alerted.map((r) => r.display_name).join(', '), time: clockAt(current.alerted_at!) })}</p>
      {:else}
        <p>{t('checkinNobody')}</p>
      {/if}
      <button class="btn btn-primary w-full text-lg py-3" disabled={busy} onclick={() => act(() => api.post<CheckIn>(`/me/check-in/${current!.id}/done`))}>{current.alerted?.length ? t('checkinTellThem') : t('checkinDone')}</button>
    </section>
  {:else}
    <p class="text-sm text-text-muted">{t('checkinIntro')}</p>
    <form class="space-y-4" onsubmit={start}>
      <fieldset>
        <legend class="font-medium mb-2">{t('checkinWithin')}</legend>
        <div class="flex flex-wrap gap-2">
          {#each LENGTHS as m (m)}
            <button type="button" class="btn {minutes === m && !custom ? 'btn-primary' : 'btn-secondary'}" aria-pressed={minutes === m && !custom} onclick={() => { minutes = m; custom = ''; }}>{m < 60 ? t('checkinMinutes', { minutes: m }) : t('checkinHours', { hours: m / 60 })}</button>
          {/each}
          <label class="flex items-center gap-2 text-sm">{t('checkinOther')} <input class="input w-24" type="number" min="15" max="1440" inputmode="numeric" placeholder={t('checkinMinutesHint')} bind:value={custom} /></label>
        </div>
      </fieldset>
      <fieldset>
        <legend class="font-medium mb-2">{t('checkinWho')}</legend>
        {#each people as p (p.user_id)}
          <label class="flex items-center gap-2 py-1"><input type="checkbox" value={p.user_id} bind:group={chosen} />{p.display_name}</label>
        {:else}
          <p class="text-sm text-text-muted">{t('checkinNoPeople')}</p>
        {/each}
      </fieldset>
      <fieldset>
        <legend class="font-medium mb-2">{t('checkinAlso')}</legend>
        <div class="flex flex-wrap items-center gap-2">
          <select class="input py-1" bind:value={watch} aria-label={t('checkinAlso')}>
            <option value="">{t('checkinOnlyTap')}</option>
            <option value="leave">{t('checkinLeave')}</option>
            <option value="arrive">{t('checkinReach')}</option>
          </select>
          {#if watch}
            <select class="input py-1" bind:value={watchPlace} aria-label={t('checkinPlace')}>
              {#if watch === 'leave'}<option value="here">{t('checkinHere')}</option>{/if}
              {#each places as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
            </select>
          {/if}
        </div>
        {#if watch}<p class="text-sm text-text-muted mt-1">{t('checkinSilent')}</p>{/if}
      </fieldset>
      <label class="block">
        <span class="block font-medium mb-1">{t('checkinNote')} <span class="text-sm text-text-muted font-normal">{t('checkinOptional')}</span></span>
        <input class="input w-full" maxlength="80" placeholder={t('checkinNoteHint')} bind:value={note} />
      </label>
      <button type="submit" class="btn btn-primary w-full text-lg py-3" disabled={busy || chosen.length === 0}>{t('checkinStart')}</button>
    </form>
  {/if}
</div>
